import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StepSection } from '@/components/step-section';
import { TextField } from '@/components/text-field';
import { ApiError } from '@/lib/api';
import { field_errors_from, form_error_message } from '@/lib/forms';
import { use_draft } from '@/features/onboarding/hooks';
import { next_module_step_after } from '@/features/onboarding/module-step-router';
import { use_food_details, use_save_food_details } from '@/features/commerce/hooks';

const FSSAI_MAX = 20;

export function ModuleFoodPage() {
  const navigate = useNavigate();
  const draft_q = use_draft();
  const saved_q = use_food_details({ enabled: !!draft_q.data });
  const save_m = use_save_food_details();
  const [fssai_license_number, set_fssai_license_number] = useState('');
  const [prefilled, set_prefilled] = useState(false);
  const [error, set_error] = useState<string | null>(null);
  const [field_errors, set_field_errors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!draft_q.isFetched) return;
    if (!draft_q.data) {
      navigate('/onboarding/start', { replace: true });
      return;
    }
    if (!draft_q.data.address_completed) {
      navigate('/onboarding/address', { replace: true });
      return;
    }
    if (!draft_q.data.module_types.includes('FOOD')) {
      navigate(next_module_step_after('FOOD', draft_q.data.module_types), {
        replace: true,
      });
    }
  }, [draft_q.isFetched, draft_q.data, navigate]);

  useEffect(() => {
    if (prefilled) return;
    if (saved_q.data) {
      set_fssai_license_number(saved_q.data.fssai_license_number ?? '');
      set_prefilled(true);
    }
  }, [saved_q.data, prefilled]);

  async function handle_submit(e: React.FormEvent) {
    e.preventDefault();
    set_error(null);

    const trimmed = fssai_license_number.trim();
    if (trimmed.length > FSSAI_MAX) {
      set_error(`FSSAI license number must be at most ${FSSAI_MAX} characters.`);
      return;
    }

    try {
      await save_m.mutateAsync({ fssai_license_number: trimmed || undefined });
      if (!draft_q.data) return;
      navigate(next_module_step_after('FOOD', draft_q.data.module_types));
    } catch (err) {
      set_field_errors(field_errors_from(err));
      set_error(
        err instanceof ApiError
          ? form_error_message(err, 'Could not save food details', { highlights_fields: true })
          : 'Could not save food details',
      );
    }
  }

  return (
    <form onSubmit={handle_submit}>
      <StepSection
        step_key="module-food"
        title="Pet food details"
        description="FSSAI (Food Safety and Standards Authority of India) license, if you have one on hand. Upload the certificate file under Documents & photos."
        error={error}
        footer={
          <Button
            type="submit"
            variant="gradient"
            disabled={save_m.isPending}
            data-testid="module-food-submit"
          >
            {save_m.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Save & continue
          </Button>
        }
      >
        <TextField
          label="FSSAI license number"
          name="fssai_license_number"
          error={field_errors.fssai_license_number}
          data-testid="field-food-fssai-license"
          value={fssai_license_number}
          onChange={(e) => set_fssai_license_number(e.target.value)}
          maxLength={FSSAI_MAX}
          placeholder="12345678901234"
          optional
          hint={`Up to ${FSSAI_MAX} characters. Skip if you don't have one yet.`}
        />
      </StepSection>
    </form>
  );
}
